import { useRef, useState } from 'react';
import {
    Badge,
    ListGroup, ListGroupItem, ListGroupItemHeading, ListGroupItemText,
} from 'reactstrap';
import { isEmpty } from 'lodash-es';
import moment from 'moment';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faTimes, faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import { appendTimeZone } from '@s20/common/util/date';
import { httpClient } from '@s20/services/httpClient';
import { Block } from '../../../common/Block';
import { ApiRoutes } from '../../../Common';
import {
    FlexBetweenRow, IconButton, SmallOutlineButton, toasty,
} from '../../../common/forms/FormElements';
import { INTERPUNCT } from '../../../common/util';
import AddressFormNew from '../../../address/AddressFormNew';
import { RatingForm } from './RatingForm';
import { EtaDisplay } from './EtaDisplay';

export const Job = ({
    selectedEquipmentId, onClose, crewEta, ...job
}) => {
    const employees = (job?.resources || []).filter((resource) => resource.type === 'Employee');
    const equipment = (job?.resources || []).filter((resource) => resource.type === 'Equipment');

    const addressFormRef = useRef(null);
    const editKindRef = useRef(null);
    const [editCountryId, setEditCountryId] = useState(null);
    const queryClient = useQueryClient();

    // Editable from request-submitted until the job is complete (timesheet submitted).
    // Works whether the item is still a job request (jobRequestId) or a dispatched job (jobId).
    const canEditLocation = !job.isComplete && (Boolean(job.jobId) || Boolean(job.jobRequestId));

    // US counties for the address form when the tenant requires county capture.
    const { data: usCounties } = useQuery({
        queryKey: ['usCounties'],
        queryFn: () => httpClient.js(ApiRoutes.USCounties.all()),
        enabled: canEditLocation,
    });

    // A dispatched job is edited via the job path; a not-yet-dispatched request via the request path.
    const updateRoute = () => (job.jobId
        ? ApiRoutes.jobRequests.updateJobLocation(job.jobId)
        : ApiRoutes.jobRequests.updateJobRequestLocation(job.jobRequestId));

    // Locations are already present on the job/request payload (GetJobRequestsForUser), so
    // no separate fetch is needed to seed the editor.
    const openLocationEditor = (kind) => {
        const address = (kind === 'meeting' ? job.meetingLocation : job.jobLocation) ?? {};
        editKindRef.current = kind;

        // The meeting location is the only one that can be unset (e.g. a legacy request
        // never had one). Fall back to the job location's country, which always exists,
        // so the state/county dropdowns aren't stranded with no country to filter by.
        const otherAddress = kind === 'meeting' ? job.jobLocation : job.meetingLocation;
        setEditCountryId(address.countryId ?? otherAddress?.countryId ?? null);
        addressFormRef.current.open(
            address,
            kind === 'meeting' ? 'Edit Meeting Location' : 'Edit Job Location',
        );
    };

    const saveLocation = useMutation({
        mutationFn: (address) => {
            // The API updates the existing Location in place from these fields only.
            // Forwarding the raw Address (which seeds id/countryId as a uuid/null) would
            // fail binding against the non-nullable LocationDTO, so send just the editable fields.
            const location = {
                address1: address.address1,
                address2: address.address2,
                city: address.city,
                state: address.state,
                usStateId: address.usStateId,
                zip: address.zip,
                usCountyId: address.usCountyId,
                timeZone: address.timeZone,
                latitude: address.latitude,
                longitude: address.longitude,
            };

            return httpClient.put(
                updateRoute(),
                editKindRef.current === 'meeting'
                    ? { meetingLocation: location }
                    : { jobLocation: location },
                httpClient.format.json,
            );
        },
        onSuccess: () => {
            toasty.success('Location Updated', 'The location has been updated.');
            queryClient.invalidateQueries({ queryKey: ['jobRequests'] });
        },
        onError: () => toasty.error('An issue arose while updating the location.'),
    });

    return (
        <div className="p-2">
            <FlexBetweenRow className="mb-2 h5">
                {job.jobNumber || ''}

                {Boolean(onClose) && (
                    <IconButton
                        onClick={onClose}
                        title="Close job"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </IconButton>
                )}
            </FlexBetweenRow>

            <EtaDisplay
                {...crewEta}
                startTime={job.startTime}
                timeZone={job.timeZone}
            />

            <div className="mb-2">
                <div className="text-muted text-uppercase small">
                    Contractor
                </div>

                <div className="font-weight-semibold">
                    {job.tenantCompanyName || '-'}
                </div>
            </div>

            <div className="mb-2">
                <div className="text-muted text-uppercase small">
                    Work Order #
                </div>

                <div className="font-weight-semibold">
                    {job.workOrderNumber || '-'}
                </div>
            </div>

            <div className="mb-2">
                <div className="text-muted text-uppercase small">
                    Purchase Order #
                </div>

                <div className="font-weight-semibold">
                    {job.purchaseOrderNumber || '-'}
                </div>
            </div>

            {Boolean(job.jobId) && (
                <div className="mb-2">
                    <div className="text-muted text-uppercase small">
                        Starts At
                    </div>

                    <div className="font-weight-semibold">
                        {appendTimeZone(moment.parseZone(job.startTime).format('h:mm A'), job.startTime, job.timeZone)}
                    </div>
                </div>
            )}

            <div className="mb-2">
                <FlexBetweenRow className="align-items-start">
                    <div>
                        <div className="text-muted text-uppercase small">
                            Meeting Location
                        </div>

                        <div className="font-weight-semibold text-break">
                            {job.meetingLocationAddress || '-'}
                        </div>
                    </div>

                    {canEditLocation && (
                        <IconButton
                            title="Edit meeting location"
                            onClick={() => openLocationEditor('meeting')}
                            size="sm"
                        >
                            <FontAwesomeIcon icon={faPenToSquare} />
                        </IconButton>
                    )}
                </FlexBetweenRow>
            </div>

            <div className="mb-2">
                <FlexBetweenRow className="align-items-start">
                    <div>
                        <div className="text-muted text-uppercase small">
                            Job Location
                        </div>

                        <div className="font-weight-semibold text-break">
                            {job.locationAddress || '-'}
                        </div>
                    </div>

                    {canEditLocation && (
                        <IconButton
                            title="Edit job location"
                            onClick={() => openLocationEditor('job')}
                            size="sm"
                        >
                            <FontAwesomeIcon icon={faPenToSquare} />
                        </IconButton>
                    )}
                </FlexBetweenRow>
            </div>

            <div className="mb-2">
                <div className="text-muted text-uppercase small">
                    Status
                </div>

                <span className="h5">
                    <Badge color={job.jobRequestStatusDisplayColor}>
                        {job.isComplete ? 'Complete' : job.jobRequestStatusName}
                    </Badge>
                </span>
            </div>

            {Boolean(job.isComplete) && (
                <RatingForm
                    {...job}
                />
            )}

            <div className="text-muted text-uppercase small">Dispatched Resources</div>
            <Block>
                {isEmpty(job.resources) ? 'Nothing has been dispatched yet' : (
                    <>
                        {employees.length > 0 && (
                            <>
                                <p>Crew</p>

                                <ListGroup>
                                    {employees.map((employee) => (
                                        <ListGroupItem key={employee.id}>
                                            <ListGroupItemHeading className="mb-0">{employee.name}</ListGroupItemHeading>

                                            {Boolean(employee.phone) && (
                                                <ListGroupItemText className="mt-2 mb-0">
                                                    <SmallOutlineButton href={`tel:${employee.phone}`}>
                                                        <FontAwesomeIcon
                                                            icon={faPhone}
                                                            className="mr-2"
                                                        />

                                                        {employee.phone}
                                                    </SmallOutlineButton>
                                                </ListGroupItemText>
                                            )}
                                        </ListGroupItem>
                                    ))}
                                </ListGroup>
                            </>
                        )}

                        {equipment.length > 0 && (
                            <>
                                <p className={employees.length && 'mt-4'}>Equipment</p>

                                <ListGroup>
                                    {equipment.map((equipment) => (
                                        <ListGroupItem
                                            key={equipment.id}
                                            active={equipment.equipmentId === selectedEquipmentId}
                                        >
                                            <ListGroupItemHeading>{equipment.equipmentDescription}</ListGroupItemHeading>

                                            <ListGroupItemText className="mb-0">
                                                {equipment.name}

                                                {Boolean(equipment.isVehicle) && (
                                                    <>
                                                        {` ${INTERPUNCT} Vehicle`}
                                                    </>
                                                )}
                                            </ListGroupItemText>
                                        </ListGroupItem>
                                    ))}
                                </ListGroup>
                            </>
                        )}
                    </>
                )}
            </Block>

            {canEditLocation && (
                <AddressFormNew
                    ref={addressFormRef}
                    id="jobLocationEditForm"
                    namePrefix=""
                    contractId={job.contractId}
                    contractUsesPrevailingWage={job.contractUsesPrevailingWage}
                    isUnionWork={job.isUnionWork}
                    workTypeId={job.workTypeId}
                    usCounties={usCounties}
                    countryId={editCountryId}
                    onSaveCallback={(address) => saveLocation.mutate(address)}
                />
            )}
        </div>
    );
};